"use client";

import { useEffect } from "react";
import { useParams } from "next/navigation";
import { AlertTriangle, Home, RotateCcw } from "lucide-react";

import PageState from "@/features/home-v2/states/PageState";
import { announce } from "@/lib/a11y/announce";

/**
 * THE root error boundary — one file, at the `[lang]` segment.
 *
 * WHY HERE AND NOWHERE ELSE. This is the highest point that still has the
 * locale in scope, so it can be localised without a dictionary fetch. Putting
 * it at `app/error.tsx` instead would lose `params.lang` and force an English
 * fallback on the Persian locale.
 *
 * WHAT IT IS NOT. It is not route coverage. Two route-level boundaries already
 * exist (`blogs`, `message`) and this phase deliberately adds none: deciding
 * which route families need their own boundary is Phase 10's, and the local
 * tier — `SectionBoundary` around a section, `DeskEmpty` around a failed
 * request — is the tier that preserves the user's context. This is the floor
 * that stops a throw becoming a blank page.
 *
 * `reset()` re-renders the segment. The route-home link exists because `reset()`
 * cannot help when the failure is deterministic — retrying the same broken
 * render forever is the trap a retry-only boundary sets.
 *
 * Copy is inline rather than dictionary-driven ON PURPOSE: this component runs
 * when something has already failed, and a dictionary import is one more thing
 * that can fail on the way to telling the user that something failed. The two
 * existing route boundaries take the same approach.
 */
export default function LangError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const params = useParams<{ lang?: string }>();
  const isFa = params?.lang === "fa";
  const lang = params?.lang === "fa" ? "fa" : "en";

  const title = isFa ? "مشکلی پیش آمد" : "Something went wrong";
  const body = isFa
    ? "این صفحه به‌درستی بارگذاری نشد. می‌توانید دوباره تلاش کنید یا به خانه برگردید."
    : "This page did not load correctly. You can try again, or go back home.";

  useEffect(() => {
    // A route boundary swapping in is a visual change with no announcement,
    // so a screen-reader user is left on a page that silently became a
    // different page.
    announce(title, "assertive");
  }, [title]);

  return (
    <PageState
      tone="error"
      glyph={<AlertTriangle aria-hidden />}
      eyebrow={isFa ? "خطا در نمایش صفحه" : "This page broke"}
      title={title}
      body={body}
      // The digest is the only reliable way for a user to report WHICH failure
      // they hit. Rendered only when Next actually produced one — never
      // fabricated, and never a stack trace.
      detail={error?.digest ? { label: isFa ? "کد خطا:" : "Error code:", value: error.digest } : undefined}
      actions={
        <>
          <button type="button" className="v2-btn v2-focus" onClick={reset}><RotateCcw aria-hidden />{isFa ? "تلاش مجدد" : "Try again"}</button>
          <button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={() => { window.location.href = `/${lang}`; }}><Home aria-hidden />{isFa ? "بازگشت به خانه" : "Back home"}</button>
        </>
      }
    />
  );
}
